import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { ErrorResponse } from 'oidc-client-ts'
import { setOidcToken } from '~shared/util/authUtil'
import { registerAuthTokenRefreshHandler } from '~shared/util/fetchUtil'
import KdsAuthorizationStore from '../Stores/KdsAuthorizationStore'
import { setDeviceContextFromToken, trackError } from '../Utils/datadog'

const interactionRequiredErrors = new Set(['interaction_required', 'login_required'])

const requiresInteractiveSignin = (error: unknown) =>
  error instanceof ErrorResponse && interactionRequiredErrors.has(error.error ?? '')

/**
 * Glue between react-oidc-context and the legacy `getAuthToken()` helper that
 * the rest of the KDS app uses. Mirrors the OIDC access token into authUtil so
 * downstream code stays unaware of OIDC, and gates rendering of children until
 * the user is authenticated (or surfaces a recoverable error).
 */
export const TokenSync = ({ children }: { children: React.ReactNode }) => {
  const auth = useAuth()
  const [redirectError, setRedirectError] = useState<Error | null>(null)

  const accessToken = auth.isAuthenticated ? auth.user?.access_token : undefined

  if (accessToken) {
    setOidcToken(accessToken)
  }

  // Decoding the JWT for telemetry tags only needs to happen when the token
  // itself changes, not on every render TokenSync's parent triggers.
  useEffect(() => {
    if (accessToken) {
      setDeviceContextFromToken(accessToken)
    }
  }, [accessToken])

  useEffect(
    () =>
      registerAuthTokenRefreshHandler(async options => {
        try {
          const user = await auth.signinSilent()
          if (user?.access_token) {
            setOidcToken(user.access_token)
            return user.access_token
          }
        } catch (error) {
          // A background refresh (allowInteractive === false) stays silent when renewal needs
          // interaction; the next user action drives the redirect instead.
          if (options?.allowInteractive !== false && requiresInteractiveSignin(error)) {
            await auth.signinRedirect()
            return undefined
          }
        }
        return undefined
      }),
    [auth]
  )

  const captureError = (error: unknown): void => {
    setRedirectError(error instanceof Error ? error : new Error(String(error)))
  }

  // With no refresh-token grant, recovery always re-runs the interactive
  // redirect that (re)activates the device.
  const retry = () => {
    setRedirectError(null)
    void auth.signinRedirect().catch(captureError)
  }

  // Start interactive activation when there is no in-memory user. The ref prevents
  // re-renders from queuing duplicate redirects.
  const hasBootstrapped = useRef(false)
  useEffect(() => {
    if (
      hasBootstrapped.current ||
      auth.isLoading ||
      auth.isAuthenticated ||
      auth.error ||
      auth.activeNavigator
    ) {
      return
    }
    hasBootstrapped.current = true
    void auth.signinRedirect().catch(captureError)
  }, [auth])

  // Clear the cached token on unmount so nothing stale lingers across
  // hot-reloads or test teardown. Empty deps = runs cleanup on unmount only.
  useEffect(() => {
    return () => {
      setOidcToken(undefined)
      KdsAuthorizationStore.reset()
    }
  }, [])

  // A pending step-up authorization can't outlive the session it was requested under.
  useEffect(() => {
    if (!auth.isAuthenticated) {
      KdsAuthorizationStore.reset()
    }
  }, [auth.isAuthenticated])

  const error = auth.error || redirectError
  useEffect(() => {
    if (error) {
      trackError(error, { source: 'authentication' })
    }
  }, [error])

  if (auth.isLoading && !auth.isAuthenticated) {
    return (
      <div className="app__loading">
        <div className="fs-loading-large"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="app__error">
        <div className="app__error__text">
          <h2>Authentication Error</h2>
          <p>{error.message}</p>
          <button className="button" onClick={retry}>
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!auth.isAuthenticated) {
    return (
      <div className="app__loading">
        <div className="fs-loading-large"></div>
      </div>
    )
  }

  return <>{children}</>
}
